// Earth orchestration (ERT-1/6). Client-side. Resolves an `EarthMeta` into the
// serializable `EarthWorld` the sim voxelizes — decoded TIL tiles + the projection
// centre + the scale-derived Y-range. The default KHFD location boots from the
// BUNDLED fixture with zero network (ERT-6); any other location REQUIRES a running
// tile service and REFUSES on failure (no fake Earth, E-38). Full-extent prefetch at
// create means a mid-play tile miss can never happen.
import { EARTH } from '../../sim/config';
import { earthRange } from '../../sim/earth/range';
import type { EarthWorld } from '../../sim/earth/earthgen';
import type { LatLon, DemSourceId } from '../../sim/earth/tiles';
import type { EarthMeta } from '../storage/saves';
import { bboxAround, geocode } from '../atlas/geocode';
import {
  loadBundledFixture, fetchMeta, prefetchExtent, idbTileCache, type EarthTileSet, type FetchOpts,
} from './tiles';

/** Everything an Earth world needs from the tile layer (ERT-1). */
export interface ResolvedEarth {
  world: EarthWorld;
  center: LatLon;
  /** Where the tiles came from — the badge provenance (ERT-3). */
  source: 'fixture' | 'service';
  /** The DEM source the badge reads (ERT-3), taken from the resolved tiles. */
  demSource: string;
  /** Negotiated contract major (TIL-3). */
  contractMajor: number;
}

/** The overworld range spec (minY/height) an Earth world at `scaleM` persists (ERT-5). */
export function earthRangeSpec(scaleM: number): { minY: number; height: number } {
  const r = earthRange(scaleM);
  return { minY: r.minY, height: r.height };
}

/** The tile-service URL for non-default Earth worlds (John's real-fetch path). Read
 *  from the Vite env; unset ⇒ '' so a non-default world refuses at boot (E-38 — never
 *  a fake Earth) rather than silently faking one. */
export function earthServiceUrl(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.['VITE_EARTHTILES_URL'] ?? '';
}

/** Build an `EarthMeta` for the create screen: a blank location is the offline KHFD
 *  default; a typed one geocodes once (ATL-7 reuse) and points at the configured tile
 *  service. Scale is metres/block (default 3 ft), editable per world (ERT-1). */
export async function earthMetaForCreate(location: string, scaleM: number): Promise<EarthMeta> {
  const loc = location.trim();
  if (loc === '' || loc === EARTH.DEFAULT.label) {
    return { lat: EARTH.DEFAULT.lat, lon: EARTH.DEFAULT.lon, label: EARTH.DEFAULT.label, scaleM, extentM: EARTH.EXTENT_M_DEFAULT, contract: { major: 1 } };
  }
  const { lat, lon } = await geocode(loc);
  return { lat, lon, label: loc, scaleM, extentM: EARTH.EXTENT_M_DEFAULT, contract: { url: earthServiceUrl(), major: 1 } };
}

/** True when a world is the offline KHFD default (boots the bundled fixture, ERT-6):
 *  the default coordinates and no explicit tile-service URL. */
export function isDefaultEarth(earth: EarthMeta): boolean {
  const near = (a: number, b: number): boolean => Math.abs(a - b) < 1e-4;
  return !earth.contract?.url && near(earth.lat, EARTH.DEFAULT.lat) && near(earth.lon, EARTH.DEFAULT.lon);
}

/** Pick the finest DEM source the service actually offers (ERT-3): 3DEP 1 m where the
 *  service has it (CONUS), else SRTM 30 m. Read off `/v1/meta.sources`. */
function pickDemSource(sources: Record<string, string>): DemSourceId {
  return sources['3dep-1m'] ? '3dep-1m' : 'srtm-30m';
}

/**
 * Resolve an Earth world. Default → the bundled KHFD fixture (offline). Any other
 * location → its tile service: negotiate `/v1/meta`, pick the DEM source, prefetch
 * the full extent (validated + cached), and REFUSE on any failure (E-38). The badge's
 * DEM source is read from the tiles that actually arrived, never assumed.
 */
export async function resolveEarth(earth: EarthMeta, opts: FetchOpts = {}): Promise<ResolvedEarth> {
  const center: LatLon = { lat: earth.lat, lon: earth.lon };
  const range = earthRange(earth.scaleM);

  let set: EarthTileSet;
  if (isDefaultEarth(earth)) {
    set = loadBundledFixture(); // zero network (ERT-6)
  } else {
    if (!earth.contract?.url) throw new Error('a non-default Earth world needs a tile service URL');
    const cache = opts.cache ?? idbTileCache();
    const withCache: FetchOpts = { ...opts, cache };
    const meta = await fetchMeta(earth.contract.url, withCache); // negotiate the contract (E-38)
    const demSource = pickDemSource(meta.sources);
    const bbox = bboxAround(center, earth.extentM * 2); // full extent (± extentM), [S,W,N,E]
    const { dem, vector } = await prefetchExtent(earth.contract.url, bbox, demSource, withCache);
    if (dem.length === 0) throw new Error('tile service returned no DEM for this extent');
    set = { meta, dem, vector, source: 'service' };
  }

  const demSource = set.dem[0]?.source ?? 'srtm-30m';
  const world: EarthWorld = {
    center, scaleM: earth.scaleM, extentM: earth.extentM, demSource, label: earth.label, range,
    vector: set.vector, dem: set.dem,
  };
  return { world, center, source: set.source, demSource, contractMajor: set.meta.contract.major };
}
